'use client';
import React, { useState } from 'react';
import { Mail, Phone, Building2, User, MessageSquare, Send, Sparkles, ArrowRight } from 'lucide-react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    type: 'demo',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const contactOptions = [
    {
      icon: Sparkles,
      title: 'Request a Demo',
      desc: 'See AgentOS in action with a personalized walkthrough tailored to your business.',
      color: '#00D4FF',
      bg: 'rgba(0,212,255,0.08)',
      border: 'rgba(0,212,255,0.20)',
      type: 'demo',
    },
    {
      icon: Mail,
      title: 'Sales Questions',
      desc: 'Have questions about pricing, features, or how AgentOS fits your workflow?',
      color: '#A78BFA',
      bg: 'rgba(167,139,250,0.08)',
      border: 'rgba(167,139,250,0.20)',
      type: 'sales',
    },
    {
      icon: Building2,
      title: 'Partnership Opportunities',
      desc: "Brokerages, teams, and technology partners—let's build something great together.",
      color: '#00D4FF',
      bg: 'rgba(0,212,255,0.06)',
      border: 'rgba(0,212,255,0.15)',
      type: 'partnership',
    },
  ];

  return (
    <section id="contact" className="py-24 relative overflow-hidden" style={{ background: '#0B0F19' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 30% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Mail size={12} />
            Get in Touch
          </div>
          <h2
            className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Let&apos;s talk about your{' '}
            <span className="gradient-cyan-text">real estate business</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Whether you want a demo, have questions, or are interested in a partnership—we&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Options */}
          <div className="space-y-4">
            {contactOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => setFormData((prev) => ({ ...prev, type: option.type }))}
                className="w-full text-left rounded-2xl p-6 transition-all duration-200"
                style={{
                  background: formData.type === option.type ? option.bg : 'rgba(255,255,255,0.04)',
                  border: formData.type === option.type
                    ? `2px solid ${option.border}`
                    : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: formData.type === option.type
                    ? `0 4px 20px ${option.color}15`
                    : 'none',
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${option.color}15`,
                      border: `1px solid ${option.color}25`,
                    }}
                  >
                    <option.icon size={20} style={{ color: option.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold" style={{ color: '#F0F4FF' }}>{option.title}</h3>
                      <ArrowRight
                        size={14}
                        style={{ color: formData.type === option.type ? option.color : 'rgba(240,244,255,0.25)' }}
                      />
                    </div>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>{option.desc}</p>
                  </div>
                </div>
              </button>
            ))}

            {/* Direct Contact Info */}
            <div
              className="rounded-2xl p-6"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <h4 className="font-bold mb-4" style={{ color: '#F0F4FF' }}>Direct Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm" style={{ color: 'rgba(240,244,255,0.65)' }}>
                  <Mail size={15} style={{ color: '#00D4FF' }} />
                  <span>hello@agentos.ai</span>
                </div>
                <div className="flex items-center gap-3 text-sm" style={{ color: 'rgba(240,244,255,0.65)' }}>
                  <Phone size={15} style={{ color: '#00D4FF' }} />
                  <span>Available via demo scheduling</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="rounded-3xl p-8"
            style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
          >
            {submitted ? (
              <div className="text-center py-12">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', boxShadow: '0 8px 24px rgba(0,212,255,0.35)' }}
                >
                  <Send size={28} className="text-black" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#F0F4FF' }}>Message Sent!</h3>
                <p className="text-sm" style={{ color: 'rgba(240,244,255,0.55)' }}>
                  Thanks for reaching out. We&apos;ll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: 'rgba(240,244,255,0.75)' }}>
                      <User size={13} className="inline mr-1.5" style={{ color: 'rgba(240,244,255,0.40)' }} />
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: 'rgba(240,244,255,0.75)' }}>
                      <Building2 size={13} className="inline mr-1.5" style={{ color: 'rgba(240,244,255,0.40)' }} />
                      Company
                    </label>
                    <input
                      type="text"
                      placeholder="Brokerage or company"
                      value={formData.company}
                      onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                      className="input-dark"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: 'rgba(240,244,255,0.75)' }}>
                      <Mail size={13} className="inline mr-1.5" style={{ color: 'rgba(240,244,255,0.40)' }} />
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="you@brokerage.com"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: 'rgba(240,244,255,0.75)' }}>
                      <Phone size={13} className="inline mr-1.5" style={{ color: 'rgba(240,244,255,0.40)' }} />
                      Phone
                    </label>
                    <input
                      type="tel"
                      placeholder="(555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                      className="input-dark"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'rgba(240,244,255,0.75)' }}>
                    <MessageSquare size={13} className="inline mr-1.5" style={{ color: 'rgba(240,244,255,0.40)' }} />
                    Message
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your business and what you're looking for..."
                    value={formData.message}
                    onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    className="input-dark resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full flex items-center justify-center gap-2 text-base"
                >
                  <Send size={16} />
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
